import { copyText } from '@/helpers';
import { _notNil } from '@/littledash';
import type { SendRulePublisher } from '@/model/Agent.model';
import { use, type FC } from 'react';
import { RiFileCopyLine } from 'react-icons/ri';
import SendPartialCheck from './SendPartialCheck';
import {
  PROVENANCE_TEXT,
  REVIEW_PENDING_TEXT,
  SendRuleProvenanceContext,
} from '@/components/Agents/SendValidator/utils/SendRuleProvenance';
import styles from './SendResults.module.scss';

interface SendRuleIdProps {
  id: string;
  publisher: SendRulePublisher;
  /** The rule's check covers only part of it. */
  partial?: boolean;
  /**
   * The CDISC Library encoding whose check produced the finding (CORE-000852), shown beside the rule
   * it cites. Only ever on a finding CDISC's own encoding produced; never on our derived or own checks.
   */
  encodingId?: string;
  className?: string;
  testId?: string;
}

/**
 * A rule's identifier, copyable, with the CDISC encoding that found it where CDISC's own encoding did,
 * who publishes it and, inside a report, how the run checked it.
 * A CDISC rule is labelled as such; one of this validator's own checks is drawn apart as an In Vivo
 * check, so nobody looks up an in-house id in CDISC's documentation and, finding nothing, distrusts
 * the published ones. Drawn on spans, since it sits in table cells and running text.
 */
const SendRuleId: FC<SendRuleIdProps> = ({
  id,
  publisher,
  partial = false,
  encodingId,
  className = '',
  testId = 'send-rule-id',
}) => (
  <span
    className={`${styles.ruleId} ${className}`}
    data-test-component="SendRuleId"
    data-test-key={id}
    data-test-element="rule-id"
    data-testid={testId}
  >
    <span className={styles.ruleIdText}>{id}</span>
    <button
      type="button"
      className={styles.ruleIdCopy}
      aria-label={`Copy rule id ${id}`}
      onClick={() => copyText(id, `Copied ${id}`)}
      data-test-element="rule-id-copy"
    >
      <RiFileCopyLine aria-hidden="true" />
    </button>
    {_notNil(encodingId) && encodingId !== id && (
      <span className={styles.encodingId} data-test-element="rule-encoding" data-test-key={encodingId}>
        <span className={styles.ruleIdText}>{encodingId}</span>
        <button
          type="button"
          className={styles.ruleIdCopy}
          aria-label={`Copy CDISC encoding id ${encodingId}`}
          onClick={() => copyText(encodingId, `Copied ${encodingId}`)}
          data-test-element="rule-encoding-copy"
        >
          <RiFileCopyLine aria-hidden="true" />
        </button>
      </span>
    )}
    {publisher === 'benchling_in_vivo' ? (
      <span className={styles.inHouseBadge} data-test-element="rule-publisher" data-test-key="benchling_in_vivo">
        In Vivo check
      </span>
    ) : (
      <span className={styles.publisherLabel} data-test-element="rule-publisher" data-test-key="cdisc">
        CDISC
      </span>
    )}
    <RuleProvenance id={id} />
    {partial && <SendPartialCheck />}
  </span>
);

/**
 * How the run checked the rule, when the report knows: CDISC's own encoding, an audited in-house check,
 * or our reading of CDISC's rule. Then, when our reading of the rule awaits a practitioner's review, that.
 */
const RuleProvenance: FC<{ id: string }> = ({ id }) => {
  const provenance = use(SendRuleProvenanceContext).get(id);
  if (provenance === undefined) {
    return null;
  }
  const text = _notNil(provenance.evaluatedBy) ? PROVENANCE_TEXT[provenance.evaluatedBy] : undefined;
  return (
    <>
      {_notNil(provenance.evaluatedBy) && _notNil(text) && (
        <span
          className={provenance.evaluatedBy === 'published_encoding' ? styles.publisherLabel : styles.inHouseBadge}
          title={text.title}
          data-test-element="rule-provenance"
          data-test-key={provenance.evaluatedBy}
        >
          {text.label}
        </span>
      )}
      {provenance.reviewState === 'pending' && (
        <span
          className={styles.reviewPending}
          title={REVIEW_PENDING_TEXT.title}
          data-test-element="rule-review"
          data-test-key="pending"
        >
          {REVIEW_PENDING_TEXT.label}
        </span>
      )}
    </>
  );
};

export default SendRuleId;
